import { useEffect, useState, useRef } from "react";
import "@style/Admin.scss";
import RightTimeApi from "@/API";
import Helpers from "../Helpers";
import { toast } from "react-toastify";
import {
  HiOutlineChevronRight,
  HiOutlineChevronLeft,
  HiOutlineChevronDoubleRight,
  HiOutlineChevronDoubleLeft,
} from "react-icons/hi";
import { MdRefresh } from "react-icons/md";
import Enums from "@/enums"; // Add this import
import AuditLogModal from "@modals/AuditLogModal";

export default function AdminSystemLog(props: any) {
  if (!props || !props.tabData) {
    console.error('AdminSystemLog: props or props.tabData is undefined');
    return null;
  }
  
  const { updateSpinner, activeTab, adminPerms } = props.tabData;

  const [logEntries, updateLogEntries] = useState<any[]>([]);
  const [pageNumber, updatePageNumber] = useState(1);
  const [logType, updateLogType] = useState("0"); // Default to "All" (0)
  const [pageSize, updatePageSize] = useState(() => {
    const storedValue = localStorage.getItem('logPerPage');
    return storedValue && !isNaN(parseInt(storedValue)) ? parseInt(storedValue) : 50;
  });
  const [pageSizeStr, updatePageSizeStr] = useState(() => {
    const storedValue = localStorage.getItem('logPerPage');
    return storedValue || "50";
  });
  const [selectedLogId, setSelectedLogId] = useState<number | null>(null);
  
  const totalPages = useRef(1);
  const lastUrl = useRef("");

  useEffect(() => {
    if (isOnThisTab()) {
      lastUrl.current = "";
      getSystemLogEntriesAsync();
    }
  }, [activeTab]);

  useEffect(() => {
    if (isOnThisTab() && (logType || pageNumber || pageSize)) {
      getSystemLogEntriesAsync();
    }
  }, [logType, pageNumber, pageSize]);

  function isOnThisTab() {
    return props && activeTab && activeTab === "systemLog";
  }

  async function getSystemLogEntriesAsync(forceRefresh = false) {
    if (!isOnThisTab()) return;

    let params = {
      page: String(pageNumber),
      pageSize: String(pageSize),
      logType: logType
    };
    
    let urlParams = new URLSearchParams(params).toString();
    let fullUrl = `/SystemLog?${urlParams}`;
    
    if (forceRefresh || fullUrl !== lastUrl.current) {
      lastUrl.current = fullUrl;
      updateSpinner(true);
      const resp = await RightTimeApi.getAsync(fullUrl);
      updateSpinner(false);
      
      if (resp && resp.ok && resp.jsonData) {
        // Update to match the actual response structure
        updateLogEntries(resp.jsonData.logs || []);
        // Calculate total pages based on total count if available
        if (resp.jsonData.total) {
          totalPages.current = Math.ceil(resp.jsonData.total / pageSize);
        } else {
          totalPages.current = 1;
        }
      } else {
        toast.error("Failed to load system logs");
      }
    }
  }

  function handleLogTypeChange(e: any) {
    updateLogType(e.target.value);
    updatePageNumber(1); // Reset to first page when changing log type
  }

  function nextPage() {
    if (pageNumber < totalPages.current) updatePageNumber(pageNumber + 1);
  }

  function prevPage() {
    if (pageNumber > 1) updatePageNumber(pageNumber - 1);
  }

  function firstPage() {
    updatePageNumber(1);
  }

  function lastPage() {
    updatePageNumber(totalPages.current);
  }

  function onChangePerPage(e: any) {
    const count = parseInt(e.target.value || pageSizeStr);
    if (!isNaN(count) && count > 0) {
      updatePageSize(count);
      localStorage.setItem('logPerPage', String(count));
      updatePageNumber(1); // Reset to first page when changing page size
    }
  }

  function refreshCurrentPage() {
    getSystemLogEntriesAsync(true);
  }

  function onLogRowClick(entry: any) {
    const id = Number(entry?.id || 0);
    if (!id) return;
    setSelectedLogId(id);
  }

  function closeAuditLogModal() {
    setSelectedLogId(null);
  }

  // Helper function to convert severity numbers to readable names
  function getSeverityName(severity: any) {
    switch (parseInt(severity)) {
      case Enums.LOG_SEVERITY.INFO: return "Info";
      case Enums.LOG_SEVERITY.DEBUG: return "Debug";
      case Enums.LOG_SEVERITY.WARNING: return "Warning";
      case Enums.LOG_SEVERITY.ERROR: return "Error";
      case Enums.LOG_SEVERITY.EXCEPTION: return "Exception";
      case Enums.LOG_SEVERITY.CRITICAL: return "Critical";
      default: return `Unknown (${severity})`;
    }
  }

  if (!isOnThisTab()) return null;

  return (
    <div className="systemLog-container">
      <div className="filters">
        <div className="filter-group">
          <label htmlFor="logType">Log Type:</label>
          <select id="logType" value={logType} onChange={handleLogTypeChange}>
            <option value="0">All</option>
            <option value={Enums.LOG_SEVERITY.INFO}>Info</option>
            <option value={Enums.LOG_SEVERITY.DEBUG}>Debug</option>
            <option value={Enums.LOG_SEVERITY.WARNING}>Warning</option>
            <option value={Enums.LOG_SEVERITY.ERROR}>Error</option>
            <option value={Enums.LOG_SEVERITY.EXCEPTION}>Exception</option>
            <option value={Enums.LOG_SEVERITY.CRITICAL}>Critical</option>
          </select>
        </div>
      </div>

      <div className="tableWrapper dimensionTable scrollable full-height systemLogTable">
        <table>
          <caption>
            <span className="title">System Log Entries</span>
            <span className="captionRight" onClick={refreshCurrentPage} title="Refresh log entries">
              <MdRefresh className="actionIcon" />
            </span>
            <div className="pageControls">
              <span onClick={firstPage}>
                <HiOutlineChevronDoubleLeft className="buttonIcons" data-testid="first-icon" />
              </span>
              <span onClick={prevPage}>
                <HiOutlineChevronLeft className="buttonIcons" data-testid="prev-icon" />
              </span>
              <span className="pageControlsText">
                Page{" "}
                <span className={`pageControlsNum digits-${String(totalPages.current).length}`}>
                  {pageNumber}
                </span>
                {" "}of{" "}
                <span className={`pageControlsNum digits-${String(totalPages.current).length}`}>
                  {totalPages.current}
                </span>
              </span>
              <span onClick={nextPage}>
                <HiOutlineChevronRight className="buttonIcons" data-testid="next-icon" />
              </span>
              <span onClick={lastPage}>
                <HiOutlineChevronDoubleRight className="buttonIcons" data-testid="last-icon" />
              </span>
              <span>
                <label htmlFor="pageSize">Show:</label>
                <input
                  id="pageSize"
                  className="perPage"
                  value={pageSizeStr}
                  onChange={(e) => updatePageSizeStr(e.target.value)}
                  onBlur={onChangePerPage}
                  onKeyDown={(e: any) => {
                    if (e.key === 'Enter') {
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                />
              </span>
            </div>
          </caption>
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Type</th>
              <th>Message</th>
              <th>Source</th>
              <th>User</th>
            </tr>
          </thead>
          <tbody>
            {logEntries.length > 0 ? (
              logEntries.map((entry, index) => (
                <tr
                  key={index}
                  className={`log-entry log-entry-clickable severity-${entry.severity}`}
                  data-log-id={entry.id}
                  onClick={() => onLogRowClick(entry)}
                >
                  <td>{Helpers.EST(entry.when)}</td>
                  <td>{getSeverityName(entry.severity)}</td>
                  <td className="left">{entry.message}</td>
                  <td>{entry.context || "N/A"}</td>
                  <td>{entry.userId || "N/A"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="center">No log entries found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AuditLogModal
        isOpen={selectedLogId !== null}
        logId={selectedLogId}
        onClose={closeAuditLogModal}
        updateSpinner={updateSpinner}
      />
    </div>
  );
}

